import { useCallback } from 'react';
import type { ComponentProps } from 'react';

import type { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer';
import { useProgressiveRenderingConfig } from '@confluence/complex-page/entry-points/useProgressiveRenderingConfig';
import { expValEquals } from '@confluence/feature-experiments';

import { AboveTheFoldReactSerializer } from './AboveTheFoldReactSerializer';
import { ProgressiveReactSerializer } from './ProgressiveReactSerializer';
import { useOnlySerializeFirstBatch } from './useOnlySerializeFirstBatch';
import { VirtualizedReactSerializer } from './VirtualizedReactSerializer';

type UseCreateSerializerProps = Parameters<typeof useProgressiveRenderingConfig>[0] & {
	adfContentId?: string;
};

type CreateSerializer = ComponentProps<typeof ADFRenderer>['createSerializer'];

export const useCreateSerializer = (props: UseCreateSerializerProps): CreateSerializer => {
	const { adfContentId, skip } = props;

	const { isComplexPage, isComplexPageReason, batchSize } = useProgressiveRenderingConfig(props);

	// Initially, only serialize the first batch. This is a performance optimization to delay
	// serialization of the remaining batches until later. The optimization is disabled if there
	// are not enough batches to warrant it.
	//
	// For SSR, subsequent serializations never happen.
	// For SPA, subsequent serializations happen after the first batch is rendered.
	const { onlySerializeFirstBatch, onBatchRendered } = useOnlySerializeFirstBatch({ adfContentId });

	/**
	 * Creates a new `Serializer` that progressively renders ADF as described at https://hello.atlassian.net/wiki/spaces/~lmarinov/pages/5177285037/COMPLEXIT+Progressive+rendering+of+ADF.
	 */
	const createComplexPageSerializer: NonNullable<CreateSerializer> = useCallback(
		(init) => {
			if (isComplexPageReason === 'TooManyExtensions') {
				return new AboveTheFoldReactSerializer(init, onlySerializeFirstBatch, onBatchRendered);
			} else {
				return new ProgressiveReactSerializer(
					init,
					batchSize,
					onlySerializeFirstBatch,
					onBatchRendered,
				);
			}
		},
		[batchSize, isComplexPageReason, onlySerializeFirstBatch, onBatchRendered],
	);

	const createVirtualizedSerializer: NonNullable<CreateSerializer> = useCallback(
		(init) => new VirtualizedReactSerializer(init),
		[],
	);

	if (skip) {
		return undefined;
	} else if (isComplexPage) {
		return createComplexPageSerializer;
	} else if (expValEquals('confluence_frontend_virtualized_tables', 'isEnabled', true)) {
		return createVirtualizedSerializer;
	} else {
		return undefined;
	}
};
